import { Box } from '@mui/material';
import type React from 'react';
import type { Section, Tender } from '../../../types/tender';
import SectionChat from '../SectionChat';
import { extractTitleTags } from './utils';

interface FloatingSectionChatProps {
  chatSectionId: string | null;
  isChatMinimized: boolean;
  setIsChatMinimized: (val: boolean) => void;
  setChatSectionId: (id: string | null) => void;
  tenderId: string;
  tender: Tender;
  canEditAllSections: boolean;
  chatContextContent: string;
  sections: Section[];
  handleApplyAIEdit: (content: string) => void;
  handleReceiveSuggestion: (content: string) => void;
  handleOpenAIEdit: (section: Section) => void;
  handleUndo: (section: Section) => void;
  handleNavigateToSection: (id: string) => void;
}

export const FloatingSectionChat: React.FC<FloatingSectionChatProps> = ({
  chatSectionId,
  isChatMinimized,
  setIsChatMinimized,
  setChatSectionId,
  tenderId,
  tender,
  canEditAllSections,
  chatContextContent,
  sections,
  handleApplyAIEdit,
  handleReceiveSuggestion,
  handleOpenAIEdit,
  handleUndo,
  handleNavigateToSection,
}) => {
  if (!chatSectionId) {
    return null;
  }

  return (
    <Box
      sx={{
        position: 'fixed',
        bottom: { xs: 10, sm: 20 },
        right: { xs: 10, sm: 20 },
        width: { xs: 'calc(100vw - 20px)', sm: 400 },
        height: isChatMinimized ? 50 : { xs: 'calc(100dvh - 20px)', sm: 600 },
        maxHeight: '100dvh',
        zIndex: 1300,
        boxShadow: 6,
        borderRadius: 2,
        bgcolor: 'background.paper',
        display: 'flex',
        flexDirection: 'column',
        transition: 'height 0.3s ease',
        overflow: 'hidden',
        border: 1,
        borderColor: 'divider',
      }}
    >
      <SectionChat
        tenderId={tenderId}
        sectionId={chatSectionId}
        canEditAllSections={canEditAllSections}
        currentContent={chatContextContent}
        onApplyEdit={handleApplyAIEdit}
        onReceiveSuggestion={handleReceiveSuggestion}
        onClose={() => setChatSectionId(null)}
        isMinimized={isChatMinimized}
        onMinimize={() => setIsChatMinimized(!isChatMinimized)}
        allSections={sections.map((s) => ({
          id: s.id,
          title: extractTitleTags(s.title).cleanTitle,
        }))}
        onSectionChange={(newId) => {
          const s = sections.find((sec) => sec.id === newId);
          if (s) {
            handleOpenAIEdit(s);
          }
        }}
        onNavigateToSection={handleNavigateToSection}
        canUndo={true}
        onUndo={() => {
          const s = sections.find((sec) => sec.id === chatSectionId);
          if (s) {
            handleUndo(s);
          }
        }}
        usedCount={tender.sectionChatCount || 0}
      />
    </Box>
  );
};
